import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import moment from 'moment';
import './UpdatePlanModal.css';
import PlanItem from './PlanItem';
import PlanScheduleGroup from './PlanScheduleGroup';
import ModalLoading from './ModalLoading';
import { fetchInstructionOptionsCached } from '../utils/instructionOptionsCache';
import { BoltIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/solid';
import { buildPlanEditorView, categoryForInstruction, getSchedulePeers, PLAN_EDITOR_GROUPS } from './planEditorViewModel';
import { shouldRenderPlanLoadingCue } from './planLoadingPresentation';

/**
 * Shared plan-editing component used by both UpdatePlanModal and StrategyModal.
 *
 * Props:
 *   planId          – the activity_plan _id to edit (required)
 *   backendService  – BackendService instance
 *   modalService    – modal service (used by PlanItem for sub-modals)
 *   focusEntryId    – optional entry _id to scroll-to / highlight on mount
 *   showHeader      – whether to show title + goal editing (default true)
 *   onClose         – optional callback fired after Save / Cancel completes
 */
const sortPlanItems = (planItems) => [...planItems].sort((a, b) => {
  const dateA = new Date(a.start_time);
  const dateB = new Date(b.start_time);
  if (dateA.getTime() === dateB.getTime()) {
    const unitMultiplier = { days: 1, weeks: 7, months: 30 };
    return (a.recurrence_number * unitMultiplier[a.recurrence_unit]) - (b.recurrence_number * unitMultiplier[b.recurrence_unit]);
  }
  return dateA.getTime() - dateB.getTime();
});

const PlanEditor = ({ planId, backendService, modalService, focusEntryId = null, showHeader = true, showLoadingCue = true, onInitialReady, onClose = null, readOnly = false }) => {
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const [title, setTitle] = useState('');
  const [goalStatement, setGoalStatement] = useState('');
  const [exercises, setExercises] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const goalStatementRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef({});
  const [highlightedEntryId, setHighlightedEntryId] = useState(null);
  const scrollRafRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [readyActivities, setReadyActivities] = useState(new Set());
  const [, setInstructionOptions] = useState({});
  const [expandedItemId, setExpandedItemId] = useState(focusEntryId);
  const [movingItemId, setMovingItemId] = useState(null);
  const [isStructuring, setIsStructuring] = useState(false);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [planDetailsExpanded, setPlanDetailsExpanded] = useState(false);
  const prevRectsRef = useRef({});

  // Editing-session state. While a session is open on the backend, the
  // scheduler ignores this plan; on save we commit, on cancel we discard
  // (which restores the snapshot taken when we opened the session).
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [metadataSaveState, setMetadataSaveState] = useState('saved');
  const [editSessionFailed, setEditSessionFailed] = useState(false);
  const pendingItemWritesRef = useRef(new Map());
  const pendingMetadataWriteRef = useRef(Promise.resolve());
  const latestMetadataSnapshotRef = useRef(null);
  const workspaceWritesRef = useRef(new Set());
  const workspaceWriteErrorRef = useRef(null);
  const structuralMutationInFlightRef = useRef(false);
  const sessionStateRef = useRef('idle'); // 'idle' | 'open' | 'closed' | 'failed'
  const canEdit = !readOnly && !editSessionFailed;
  const editorLocked = !canEdit || isSaving || isCancelling || isStructuring;
  itemsRef.current = items;
  const markDirty = useCallback(() => { if (canEdit) setIsDirty(true); }, [canEdit]);
  const reportWorkspaceError = useCallback((message) => setWorkspaceError(String(message || 'Something went wrong. Try again.')), []);
  const registerPendingCommit = useCallback((entryId, controller) => {
    if (controller) pendingItemWritesRef.current.set(String(entryId), controller);
    else pendingItemWritesRef.current.delete(String(entryId));
  }, []);
  const trackWorkspaceWrite = useCallback((promise) => {
    workspaceWriteErrorRef.current = null;
    workspaceWritesRef.current.add(promise);
    promise.then(
      () => workspaceWritesRef.current.delete(promise),
      error => {
        workspaceWriteErrorRef.current = error;
        workspaceWritesRef.current.delete(promise);
      },
    );
    return promise;
  }, []);
  const settlePendingItemWrites = useCallback(async (mode) => {
    const controllers = [...pendingItemWritesRef.current.values()];
    await Promise.all(controllers.map(controller => controller?.[mode]?.()));
    await Promise.allSettled([...workspaceWritesRef.current]);
    if (workspaceWriteErrorRef.current) {
      if (mode === 'cancel') workspaceWriteErrorRef.current = null;
      else throw workspaceWriteErrorRef.current;
    }
  }, []);
  const runStructuralMutation = useCallback(async (operation) => {
    if (structuralMutationInFlightRef.current) return false;
    structuralMutationInFlightRef.current = true;
    setIsStructuring(true);
    workspaceWriteErrorRef.current = null;
    try {
      await settlePendingItemWrites('flush');
      await operation();
      return true;
    } finally {
      structuralMutationInFlightRef.current = false;
      setIsStructuring(false);
    }
  }, [settlePendingItemWrites]);
  const persistMetadataSnapshot = useCallback((snapshot) => {
    latestMetadataSnapshotRef.current = snapshot;
    setMetadataSaveState('saving');
    const write = pendingMetadataWriteRef.current
      .catch(() => {})
      .then(() => backendService.updatePlanDetails(planId, snapshot));
    pendingMetadataWriteRef.current = write;
    write.then(() => {
      if (pendingMetadataWriteRef.current === write) {
        setMetadataSaveState('saved');
        setWorkspaceError('');
      }
    }).catch(error => {
      console.error('[PlanEditor] Failed to update plan details:', error);
      if (pendingMetadataWriteRef.current === write) {
        setMetadataSaveState('error');
        setWorkspaceError('Could not save the plan details. Your text is still here; Save plan or close will retry.');
      }
    });
    return write;
  }, [backendService, planId]);
  const flushPendingMetadata = useCallback(async () => {
    try {
      await pendingMetadataWriteRef.current;
    } catch (error) {
      const snapshot = latestMetadataSnapshotRef.current;
      if (!snapshot) throw error;
      await persistMetadataSnapshot(snapshot);
    }
  }, [persistMetadataSnapshot]);

  // ─── Textarea auto-height ───
  const adjustTextareaHeight = useCallback(() => {
    if (goalStatementRef.current) {
      goalStatementRef.current.style.height = 'auto';
      goalStatementRef.current.style.height = `${goalStatementRef.current.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(adjustTextareaHeight, 0);
    }
  }, [goalStatement, adjustTextareaHeight, isLoading]);

  // ─── Regenerate workouts ───
  const handleRefreshWorkouts = useCallback(async () => {
    if (readOnly) return;
    setIsRegenerating(true);
    setWorkspaceError('');
    try {
      await runStructuralMutation(async () => {
        markDirty();
        await trackWorkspaceWrite(backendService.refreshWorkouts(planId));
        const data = await backendService.fetchPlanItems(planId);
        itemsRef.current = data;
        setItems(data);
      });
    } catch (error) {
      console.error('[PlanEditor] Failed to refresh workouts:', error);
      setWorkspaceError('Could not regenerate the plan. Existing activities were left unchanged.');
    } finally {
      setIsRegenerating(false);
    }
  }, [backendService, planId, markDirty, readOnly, runStructuralMutation, trackWorkspaceWrite]);

  // ─── Save / Cancel editing session ───
  // Both Save and Cancel close the editor (via onClose). The next time the
  // editor is opened, init re-fetches from the backend, so a discarded
  // session naturally shows the live (unchanged) entries.
  const handleSaveEdits = useCallback(async () => {
    if (!planId || !canEdit) return;
    setIsSaving(true);
    setWorkspaceError('');
    try {
      await settlePendingItemWrites('flush');
      await flushPendingMetadata();
      if (sessionStateRef.current === 'open') {
        await backendService.commitPlanEditSession(planId);
        sessionStateRef.current = 'closed';
      }
      setIsDirty(false);
      window.dispatchEvent(new Event('regimen:plan:updated'));
      if (onClose) onClose();
    } catch (err) {
      console.error('[PlanEditor] Failed to save plan:', err);
      setWorkspaceError('Could not save the plan. Your changes are still here—check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  }, [backendService, planId, onClose, canEdit, settlePendingItemWrites, flushPendingMetadata]);

  const handleCancelEdits = useCallback(async () => {
    if (!planId || !canEdit) return;
    if (isDirty && !window.confirm('Discard activity changes? The plan title and goal are saved automatically and will stay.')) return;
    setIsCancelling(true);
    setWorkspaceError('');
    try {
      await settlePendingItemWrites('cancel');
      await flushPendingMetadata();
      if (sessionStateRef.current === 'open') {
        await backendService.discardPlanEditSession(planId);
        sessionStateRef.current = 'closed';
      }
      setIsDirty(false);
      window.dispatchEvent(new Event('regimen:plan:updated'));
      if (onClose) onClose();
    } catch (err) {
      console.error('[PlanEditor] Failed to cancel plan edits:', err);
      setWorkspaceError('Could not discard these changes. Nothing was closed; try again.');
    } finally {
      setIsCancelling(false);
    }
  }, [backendService, planId, isDirty, onClose, canEdit, settlePendingItemWrites, flushPendingMetadata]);

  // ─── Notify activities of plan changes on unmount ───
  // If the user closes the editor without explicitly clicking Save or Cancel,
  // treat that as an implicit Save: commit the editing session so the
  // scheduler resumes with whatever state they left behind. The Cancel
  // button is the explicit way to discard changes.
  useEffect(() => {
    return () => {
      if (sessionStateRef.current === 'open' && planId) {
        sessionStateRef.current = 'closing';
        Promise.resolve()
          .then(() => settlePendingItemWrites('flush'))
          .then(() => flushPendingMetadata())
          .then(() => backendService.commitPlanEditSession(planId))
          .then(() => window.dispatchEvent(new Event('regimen:plan:updated')))
          .catch(err => console.error('[PlanEditor] Auto-commit on unmount failed:', err));
      } else {
        window.dispatchEvent(new Event('regimen:plan:updated'));
      }
    };
  }, [backendService, planId, flushPendingMetadata, settlePendingItemWrites]);

  useEffect(() => {
    if (!isLoading) onInitialReady?.();
  }, [isLoading, onInitialReady]);

  // ─── Initial data load ───
  useEffect(() => {
    if (!planId) {
      setIsLoading(false);
      return;
    }

    let active = true;
    setIsLoading(true);

    const init = async () => {
      try {
        if (readOnly) {
          const [exercisesData, itemsData, detailsData] = await Promise.all([
            backendService.fetchExercises(),
            backendService.fetchPlanItems(planId),
            backendService.fetchPlanDetails(planId)
          ]);
          if (!active) return;
          setExercises(exercisesData || []);
          setItems(sortPlanItems(itemsData || []));
          setTitle(detailsData?.title || '');
          setGoalStatement(detailsData?.goal_statement || '');
          return;
        }
        // Begin an editing session so the scheduler stops consuming this
        // plan's entries until the user saves. Idempotent on the backend.
        try {
          await backendService.beginPlanEditSession(planId);
          if (!active) return;
          sessionStateRef.current = 'open';
        } catch (err) {
          console.error('[PlanEditor] Failed to begin edit session:', err);
          sessionStateRef.current = 'failed';
          if (active) {
            setEditSessionFailed(true);
            setWorkspaceError('Editing is unavailable because a safe plan session could not be opened. Nothing can be changed; close and try again.');
          }
        }

        const [exercisesData, itemsData, detailsData] = await Promise.all([
          backendService.fetchExercises().catch(err => {
            console.error('[PlanEditor] Error fetching exercises:', err);
            return [];
          }),
          backendService.fetchPlanItems(planId).catch(err => {
            console.error('[PlanEditor] Error fetching plan items:', err);
            if (active) setWorkspaceError('Could not load the plan activities. Try closing and reopening the editor.');
            return [];
          }),
          backendService.fetchPlanDetails(planId).catch(err => {
            console.error('[PlanEditor] Error fetching plan details:', err);
            return {};
          })
        ]);

        if (!active) return;
        setExercises(exercisesData);
        setItems(sortPlanItems(itemsData));
        setTitle(detailsData.title || '');
        setGoalStatement(detailsData.goal_statement || '');
        setReadyActivities(new Set());
      } catch (error) {
        console.error('[PlanEditor] Initialization error:', error);
        if (active) setWorkspaceError('Could not load this plan. Try closing and reopening the editor.');
      } finally {
        if (active) setIsLoading(false);
      }
    };

    init();
    return () => { active = false; };
  }, [planId, backendService, readOnly]);

  // ─── Load instruction options ───
  useEffect(() => {
    if (!planId) return;
    const loadInstructionOptions = async () => {
      try {
        const options = await fetchInstructionOptionsCached(backendService, planId);
        setInstructionOptions(options || {});
      } catch (error) {
        console.error('[PlanEditor] Failed to load instruction options:', error);
        setInstructionOptions({});
      }
    };
    loadInstructionOptions();
  }, [backendService, planId]);

  // ─── Scroll to focused entry ───
  const cancelScheduledScroll = () => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };

  useEffect(() => {
    hasScrolledRef.current = false;
  }, [focusEntryId]);

  const scheduleScrollToFocusedEntry = useCallback(() => {
    if (hasScrolledRef.current) return;
    cancelScheduledScroll();
    if (!focusEntryId) return;
    const allReady = items.length > 0 && readyActivities.size >= items.length;
    if (!allReady) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = requestAnimationFrame(() => {
        const target = itemRefs.current[focusEntryId];
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setHighlightedEntryId(focusEntryId);
          setTimeout(() => setHighlightedEntryId(null), 1500);
          hasScrolledRef.current = true;
        }
        scrollRafRef.current = null;
      });
    });
  }, [focusEntryId, items.length, readyActivities]);

  useEffect(() => {
    scheduleScrollToFocusedEntry();
    return cancelScheduledScroll;
  }, [items, scheduleScrollToFocusedEntry]);

  // ─── Activity ready callback ───
  const handleActivityReady = useCallback((entryId) => {
    setReadyActivities((prev) => {
      if (prev.has(entryId)) return prev;
      const next = new Set(prev);
      next.add(entryId);
      return next;
    });
  }, []);

  // ─── Expand / collapse ───
  const handleToggleExpand = useCallback((itemId) => {
    setExpandedItemId(prev => prev === itemId ? null : itemId);
  }, []);

  // ─── Close/regeneration guards ───
  useEffect(() => {
    if (!modalService) return undefined;
    if (isRegenerating || isSaving || isCancelling || isStructuring) modalService.disableClose?.();
    else modalService.enableClose?.();
    // Save/discard can unmount the editor before the final busy=false render.
    // Release the shared close lock on cleanup so the next modal stays usable.
    return () => modalService.enableClose?.();
  }, [isRegenerating, isSaving, isCancelling, isStructuring, modalService]);

  useEffect(() => {
    if (!modalService?.onBeforeClose || readOnly) return undefined;
    const closeSafely = async () => {
      if (sessionStateRef.current !== 'open') return true;
      setIsSaving(true);
      setWorkspaceError('');
      try {
        await settlePendingItemWrites('flush');
        await flushPendingMetadata();
        await backendService.commitPlanEditSession(planId);
        sessionStateRef.current = 'closed';
        window.dispatchEvent(new Event('regimen:plan:updated'));
        return true;
      } catch (error) {
        console.error('[PlanEditor] Could not commit before closing:', error);
        setWorkspaceError('Could not save before closing. The editor is still open; try Save plan again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    };
    const unregister = modalService.onBeforeClose(closeSafely);
    return () => unregister?.();
  }, [backendService, modalService, planId, readOnly, settlePendingItemWrites, flushPendingMetadata]);

  // ─── Move / reorder ───
  // Reordering operates across all items in the same instruction-type group
  // (e.g. all "Workouts"), regardless of recurrence cadence — otherwise a
  // newly-added item (which defaults to today's weekday) can't be reordered
  // against pre-existing items scheduled on a different weekday.
  const handleMoveItem = async (itemId, direction) => {
    if (structuralMutationInFlightRef.current) return;
    try {
      await runStructuralMutation(async () => {
        const currentItems = itemsRef.current;
        const item = currentItems.find(candidate => candidate._id === itemId);
        if (!item) return;
        const categoryItems = currentItems.filter(candidate => categoryForInstruction(candidate.instruction) === categoryForInstruction(item.instruction));
        const peers = getSchedulePeers(categoryItems, item);
        const peerIndex = peers.findIndex(candidate => candidate._id === itemId);
        const targetPeer = peers[peerIndex + (direction < 0 ? -1 : 1)];
        if (!targetPeer) return;
        const currentIndex = currentItems.findIndex(candidate => candidate._id === itemId);
        const targetIndex = currentItems.findIndex(candidate => candidate._id === targetPeer._id);
        if (currentIndex < 0 || targetIndex < 0) return;

        const rects = {};
        for (const id in itemRefs.current) {
          if (itemRefs.current[id]) rects[id] = itemRefs.current[id].getBoundingClientRect();
        }
        prevRectsRef.current = rects;
        setMovingItemId(itemId);

        const reorderedItems = [...currentItems];
        [reorderedItems[currentIndex], reorderedItems[targetIndex]] = [reorderedItems[targetIndex], reorderedItems[currentIndex]];
        setWorkspaceError('');
        markDirty();
        itemsRef.current = reorderedItems;
        setItems(reorderedItems);
        try {
          await trackWorkspaceWrite(backendService.reorderPlanEntries(planId, reorderedItems.map(entry => entry._id)));
        } catch (error) {
          itemsRef.current = currentItems;
          setItems(currentItems);
          throw error;
        }
      });
    } catch (error) {
      console.error('Failed to save order:', error);
      setWorkspaceError('Could not reorder the activities. The previous order was restored.');
    } finally {
      setTimeout(() => setMovingItemId(null), 500);
    }
  };

  // ─── FLIP animation ───
  useLayoutEffect(() => {
    if (Object.keys(prevRectsRef.current).length === 0) return;

    const animations = [];
    items.forEach(item => {
      const domNode = itemRefs.current[item._id];
      const prevRect = prevRectsRef.current[item._id];
      if (domNode && prevRect) {
        const newRect = domNode.getBoundingClientRect();
        const dy = prevRect.top - newRect.top;
        if (dy !== 0) {
          domNode.style.transform = `translateY(${dy}px)`;
          domNode.style.transition = 'none';
          animations.push(() => {
            domNode.style.transform = '';
            domNode.style.transition = 'transform 0.5s ease';
          });
        }
      }
    });

    prevRectsRef.current = {};
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        animations.forEach(anim => anim());
      });
    });
  }, [items]);

  // ─── Remove item ───
  const handleRemoveItem = async (itemId) => {
    if (structuralMutationInFlightRef.current) return;
    const confirmDelete = window.confirm('Delete this activity from the plan? You can discard the editing session to restore it.');
    if (!confirmDelete) return;

    try {
      await runStructuralMutation(async () => {
        const visibleIds = [...(listRef.current?.querySelectorAll('li:not([hidden])') || [])]
          .map(row => row.dataset.itemId)
          .filter(Boolean);
        const visibleIndex = visibleIds.indexOf(String(itemId));
        const nextFocusId = visibleIds[visibleIndex + 1] || visibleIds[visibleIndex - 1] || null;
        markDirty();
        setWorkspaceError('');
        await trackWorkspaceWrite(backendService.deletePlanItem(planId, itemId));
        pendingItemWritesRef.current.delete(String(itemId));
        const remainingItems = itemsRef.current.filter((item) => item._id !== itemId);
        itemsRef.current = remainingItems;
        setItems(remainingItems);
        requestAnimationFrame(() => {
          const nextDisclosure = nextFocusId
            ? listRef.current?.querySelector(`[data-item-id="${nextFocusId}"] .plan-item-disclosure-button`)
            : null;
          (nextDisclosure || listRef.current?.querySelector('.plan-group:not([hidden]) .plan-group-heading'))?.focus();
        });
      });
    } catch (error) {
      console.error('[PlanEditor] Failed to remove item from plan:', error);
      setWorkspaceError('Could not delete the activity. The plan was not changed.');
    }
  };

  // ─── Add item ───
  const handleAddButtonClick = async () => {
    if (structuralMutationInFlightRef.current) return;
    const newItem = {
      start_time: moment().startOf('day'),
      recurrence_number: '1',
      recurrence_unit: 'weeks',
      user_can_modify: true,
      autogeneration_hint: ''
    };

    try {
      await runStructuralMutation(async () => {
        markDirty();
        setWorkspaceError('');
        const response = await trackWorkspaceWrite(backendService.savePlanItem(planId, null, newItem));
        handleUpdateItem(response);
      });
    } catch (error) {
      console.error('[PlanEditor] Failed to add new item:', error);
      setWorkspaceError('Could not add the activity. The plan was not changed.');
    }
  };

  const getDefaultStartDay = () => {
    if (items.length > 0) {
      return moment(items[items.length - 1].startDay, 'dddd, MMMM Do').format('YYYY-MM-DD');
    }
    return moment().format('YYYY-MM-DD');
  };

  // ─── Update plan details ───
  // Plan title and goal are intentionally saved immediately, outside the
  // entry editing session. Full snapshots are serialized to prevent rapid
  // typing from resolving out of order; Save/close retries the latest failure.
  const queuePlanDetails = (nextTitle, nextGoalStatement) => {
    if (!canEdit) return;
    setWorkspaceError('');
    persistMetadataSnapshot({
      title: nextTitle,
      goal_statement: nextGoalStatement,
    });
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    queuePlanDetails(newTitle, goalStatement);
  };

  const handleGoalStatementChange = (e) => {
    const newGoalStatement = e.target.value;
    setGoalStatement(newGoalStatement);
    queuePlanDetails(title, newGoalStatement);
  };

  // ─── Handle item update / insert ───
  // For in-place edits we preserve the user's manual ordering. Newly inserted
  // items get sorted in (so they land at a sensible default position based on
  // their start_time / cadence). Manual moves via the up/down arrows are
  // handled separately and never re-sort.
  const handleUpdateItem = useCallback((newItem) => {
    const currentItems = itemsRef.current;
    const editingIndex = currentItems.findIndex(item => item._id === newItem._id);
    let nextItems;
    if (editingIndex !== -1) {
      nextItems = [...currentItems];
      nextItems[editingIndex] = newItem;
    } else {
      setHighlightedEntryId(newItem._id);
      setTimeout(() => setHighlightedEntryId(null), 1500);
      nextItems = sortPlanItems([...currentItems, newItem]);
    }
    itemsRef.current = nextItems;
    setItems(nextItems);

    setTimeout(() => {
      const itemElement = listRef.current?.querySelector(`[data-item-id="${newItem._id}"]`);
      if (itemElement) {
        const rect = itemElement.getBoundingClientRect();
        const inViewport = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
        if (!inViewport) itemElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, []);

  const workspaceView = useMemo(
    () => buildPlanEditorView(items, { query, filter: activeFilter }),
    [items, query, activeFilter]
  );
  const canonicalView = useMemo(() => buildPlanEditorView(items), [items]);
  const visibleItemIds = useMemo(
    () => new Set(workspaceView.groups.flatMap(group => group.items.map(item => String(item._id)))),
    [workspaceView.groups]
  );
  const filterOptions = useMemo(() => ([
    { id: 'all', label: 'All', count: workspaceView.counts.all },
    ...PLAN_EDITOR_GROUPS.filter(group => workspaceView.counts[group.id] > 0).map(group => ({
      id: group.id, label: group.label, count: workspaceView.counts[group.id],
    })),
  ]), [workspaceView.counts]);
  const toggleGroup = (groupId) => setCollapsedGroups(previous => {
    const next = new Set(previous);
    if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
    return next;
  });

  // ─── RENDER ───

  if (isLoading && !showLoadingCue) return null;

  if (shouldRenderPlanLoadingCue({ isLoading, showLoadingCue })) {
    return (
      <div className="update-plan-modal loading">
        <ModalLoading text="Loading plan data..." />
      </div>
    );
  }

  if (!planId) {
    return (
      <div className="update-plan-modal">
        <div className="choose-plan-empty">
          <p>No plan selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`update-plan-modal plan-editor-workspace ${isRegenerating ? 'regenerating' : ''}`}>
      <header className="plan-workspace-header">
        <div className="plan-workspace-heading">
          <div>
            <h2>{showHeader ? (title.trim() || 'Untitled plan') : 'Plan activities'}</h2>
            <p>{workspaceView.counts.all} scheduled activit{workspaceView.counts.all === 1 ? 'y' : 'ies'}</p>
          </div>
          <span
            className={`plan-workspace-status ${editSessionFailed || metadataSaveState === 'error' ? 'is-error' : (isDirty ? 'is-dirty' : '')}`}
            role="status"
            aria-live="polite"
          >
            {readOnly
              ? 'READ ONLY'
              : (editSessionFailed
                ? 'EDITING UNAVAILABLE'
                : (metadataSaveState === 'saving'
                  ? 'SAVING DETAILS'
                  : (metadataSaveState === 'error' ? 'DETAILS NOT SAVED' : (isDirty ? 'UNSAVED' : 'SAVED'))))}
          </span>
        </div>
        {showHeader && (
          <>
            <button
              type="button"
              className="plan-details-summary"
              onClick={() => setPlanDetailsExpanded(open => !open)}
              aria-expanded={planDetailsExpanded}
            >
              <span>{goalStatement.trim() || 'Add a goal so the plan has a clear programming target.'}</span>
              <strong>{planDetailsExpanded ? 'Hide details' : (canEdit ? 'Edit details' : 'View details')}</strong>
            </button>
            {planDetailsExpanded && (
              <div className="update-plan-meta plan-details-editor">
                <div className="form-group">
                  <label className="update-plan-label" htmlFor="plan-editor-title">Plan title</label>
                  <input id="plan-editor-title" type="text" placeholder="e.g. Hypertrophy + cardio" value={title} onChange={handleTitleChange} disabled={editorLocked || isRegenerating} />
                </div>
                <div className="form-group">
                  <label className="update-plan-label" htmlFor="plan-editor-goal">Goal</label>
                  <textarea ref={goalStatementRef} id="plan-editor-goal" placeholder="What should this plan optimize for?" value={goalStatement} onChange={handleGoalStatementChange} disabled={editorLocked || isRegenerating} onInput={adjustTextareaHeight} />
                </div>
                <p className={`plan-details-autosave ${metadataSaveState === 'error' ? 'is-error' : ''}`}>
                  {metadataSaveState === 'saving' ? 'Saving plan details…' : (metadataSaveState === 'error' ? 'Plan details not saved yet. Save plan or close to retry.' : 'Plan title and goal save automatically.')}
                </p>
              </div>
            )}
          </>
        )}
      </header>

      <div className="plan-workspace-tools">
        <label className="plan-workspace-search">
          <MagnifyingGlassIcon aria-hidden="true" />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Find an activity" aria-label="Find an activity" />
        </label>
        {canEdit && (
          <div className="plan-workspace-tool-actions">
            {items.length > 0 && (
              <button type="button" onClick={handleRefreshWorkouts} disabled={editorLocked || isRegenerating} className="plan-workspace-regenerate" title="Regenerate auto-generated activities" aria-label="Regenerate auto-generated activities">
                <BoltIcon aria-hidden="true" /> <span>{isRegenerating ? 'Working…' : 'Regenerate'}</span>
              </button>
            )}
            <button type="button" onClick={handleAddButtonClick} disabled={editorLocked || isRegenerating} className="plan-workspace-add">
              <PlusIcon aria-hidden="true" /> Add
            </button>
          </div>
        )}
      </div>

      <nav className="plan-workspace-filters" aria-label="Activity type filters">
        {filterOptions.map(option => (
          <button key={option.id} type="button" className={activeFilter === option.id ? 'is-active' : ''} onClick={() => setActiveFilter(option.id)} aria-pressed={activeFilter === option.id}>
            {option.label} <span>{option.count}</span>
          </button>
        ))}
      </nav>

      <div className="plan-items-container" ref={listRef}>
        {workspaceView.groups.length === 0 && (
          <div className="plan-workspace-empty">
            <strong>No matching activities</strong>
            <span>Try another search or activity type.</span>
            {(query || activeFilter !== 'all') && <button type="button" onClick={() => { setQuery(''); setActiveFilter('all'); }}>Clear filters</button>}
          </div>
        )}
        {canonicalView.groups.map(group => {
          const collapsed = !query.trim() && collapsedGroups.has(group.id);
          const groupVisible = group.items.some(item => visibleItemIds.has(String(item._id)));
          return (
            <PlanScheduleGroup
              key={group.id}
              group={group}
              visibleItemIds={visibleItemIds}
              collapsed={collapsed}
              hidden={!groupVisible}
              onToggle={() => toggleGroup(group.id)}
              getItemProps={item => ({
                ref: element => { itemRefs.current[item._id] = element; },
                className: movingItemId === item._id ? 'highlight-move' : '',
              })}
              renderItem={(item, { weeklyAgenda }) => {
                const categoryItems = items.filter(peer => categoryForInstruction(peer.instruction) === categoryForInstruction(item.instruction));
                const schedulePeers = getSchedulePeers(categoryItems, item);
                const scheduleIndex = schedulePeers.findIndex(peer => peer._id === item._id);
                return (
                  <div className="plan-editor-item-main">
                    <PlanItem
                      onUpdateItem={handleUpdateItem}
                      editingItem={item}
                      defaultStartDay={getDefaultStartDay()}
                      planId={planId}
                      exercises={exercises}
                      modalService={modalService}
                      backendService={backendService}
                      disabled={editorLocked || isRegenerating}
                      isRegenerating={isRegenerating || isStructuring || isSaving || isCancelling}
                      onRemoveItem={() => handleRemoveItem(item._id)}
                      highlighted={highlightedEntryId === item._id}
                      isExpanded={expandedItemId === item._id}
                      onToggleExpand={() => handleToggleExpand(item._id)}
                      onActivityReady={handleActivityReady}
                      readOnly={editorLocked}
                      canMoveUp={!movingItemId && scheduleIndex > 0}
                      canMoveDown={!movingItemId && scheduleIndex >= 0 && scheduleIndex < schedulePeers.length - 1}
                      showMoveActions={schedulePeers.length > 1}
                      weeklyAgenda={weeklyAgenda}
                      onMoveUp={() => handleMoveItem(item._id, -1)}
                      onMoveDown={() => handleMoveItem(item._id, 1)}
                      onError={reportWorkspaceError}
                      onDirty={markDirty}
                      registerPendingCommit={registerPendingCommit}
                    />
                  </div>
                );
              }}
            />
          );
        })}
      </div>

      {!canEdit && workspaceError && <div className="plan-workspace-error" role="alert">{workspaceError}</div>}
      {canEdit ? (
        <footer className="plan-editor-action-shell">
          {workspaceError && <div className="plan-workspace-error" role="alert">{workspaceError}</div>}
          <div className="plan-editor-action-help">{isDirty ? 'Review and save to activate activity changes.' : 'Activities are up to date. Title and goal save automatically.'}</div>
          <div className="plan-editor-action-bar">
            <button className="plan-editor-btn cancel" onClick={handleCancelEdits} disabled={isSaving || isCancelling || isStructuring} title={isDirty ? 'Discard activity changes; plan title and goal remain saved' : 'Close without activity changes'}>
              {isCancelling ? 'Discarding…' : (isDirty ? 'Discard' : 'Close')}
            </button>
            <button className="plan-editor-btn save" onClick={handleSaveEdits} disabled={(!isDirty && metadataSaveState !== 'error') || isSaving || isCancelling || isStructuring}>
              {isSaving ? 'Saving…' : 'Save plan'}
            </button>
          </div>
        </footer>
      ) : (
        <div className="plan-editor-action-help">{readOnly ? 'This dojo plan is shared. Only the dojo leader can change it.' : 'Editing is disabled until a safe editing session can be opened.'}</div>
      )}
    </div>
  );
};

export default PlanEditor;
